import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, distinct, last, map, mergeMap, Observable, of, withLatestFrom } from 'rxjs';
import { client } from '../..';
import * as actions from './actions';
import { DirectorySetting, DirectorySettingValue } from './actions';
import { retry } from 'projects/angular-clarity/src/app/pipes/retry.pipe';
import { TenantAjaxService } from 'projects/angular-clarity/src/app/services/ajax/tenant-ajax.service';

@Injectable()
export class GraphDirectorySettingsEffects {
    loadGraphDirectorySettings$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.loadDirectorySettings),
            distinct((action) => action._tenant),
            mergeMap(({ _tenant }) =>
                this.get(_tenant).pipe(
                    retry(5000, 3, 'graph timeout'),
                    last(),
                    map((settings: DirectorySetting[]) => actions.loadDirectorySettingsSuccess({ _tenant, settings })),
                    catchError((error) => of(actions.loadDirectorySettingsFailure({ _tenant, error }))),
                ),
            ),
        ),
    );

    createGraphDirectorySetting$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.createDirectorySetting),
            mergeMap(({ _tenant, templateId, values }) =>
                this.post(_tenant, templateId, values).pipe(
                    withLatestFrom(this.store.select(client(_tenant).graph.directorySettings.all)),
                    map(([setting, current]) =>
                        actions.updateDirectorySettingSuccess({ _tenant, settings: [...current, setting] }),
                    ),
                    catchError((error) => of(actions.updateDirectorySettingFailure({ _tenant, error }))),
                ),
            ),
        ),
    );

    updateGraphDirectorySetting$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.updateDirectorySetting),
            mergeMap(({ _tenant, id, values }) =>
                this.patch(_tenant, id, values).pipe(
                    withLatestFrom(this.store.select(client(_tenant).graph.directorySettings.all)),
                    map(([, current]) => {
                        // Reflect the values we just successfully wrote rather than re-fetching.
                        // Microsoft Graph directory settings are eventually consistent, so an
                        // immediate GET after the PATCH frequently returns stale values, leaving
                        // the store (and UI) showing the pre-remediation state until a page reload.
                        // Merging the patched values into the cached setting updates the store
                        // synchronously and accurately.
                        const settings = current.map((setting) =>
                            setting.id === id ? { ...setting, values } : setting,
                        );
                        return actions.updateDirectorySettingSuccess({ _tenant, settings });
                    }),
                    catchError((error) => of(actions.updateDirectorySettingFailure({ _tenant, error }))),
                ),
            ),
        ),
    );

    private get(tenant: string): Observable<DirectorySetting[]> {
        return this.ajax
            .get<{ value: DirectorySetting[] }>(tenant, '/api/microsoft/graph/settings')
            .pipe(map((res) => res.value));
    }

    private post(tenant: string, templateId: string, values: DirectorySettingValue[]): Observable<DirectorySetting> {
        return this.ajax.post<DirectorySetting>(tenant, '/api/microsoft/graph/settings', { templateId, values });
    }

    private patch(tenant: string, id: string, values: DirectorySettingValue[]): Observable<unknown> {
        return this.ajax.patch(tenant, `/api/microsoft/graph/settings/${id}`, { values });
    }

    constructor(
        private actions$: Actions,
        private ajax: TenantAjaxService,
        private store: Store,
    ) {}
}
